import "./App.css";
import { useEffect } from "react";
import { Route, useLocation, Routes } from "react-router-dom";
import LoginPage from "./pages/LoginPage/LoginPage.tsx";
import ProfilePage from "./pages/Profile/ProfilePage.tsx";
import Dashboard from "./pages/DashBoard/DashBoard.tsx";
import { UserProvider } from "./context/UserContext.tsx";
import { AuthProvider } from "./context/AuthContext.tsx";
import { ClientQueryContextProvider } from "./context/ClientQueryContext.tsx";
import { WebSocketContextProvider } from "./context/WebSocketContext.tsx";
import { DevicesContextProvider } from "./context/DevicesContext.tsx";
import { LiveViewDataContextProvider } from "./context/LiveViewDataContext.tsx";
import SuspiciousAlert from "./pages/SuspiciousAlertsPageCopy/SuspiciousAlert.tsx";
import TriggerAlertPage from "./pages/TriggerAlertsPage/TriggerAlertPage.tsx";
import ChangePassword from "./pages/ChangePass/ChangePassword.tsx";
import ForgotPwd from "./pages/ForgotPwd/RequestPwdResetLink.tsx";
import ResetPwd from "./pages/ForgotPwd/ResetPwd.tsx";
import { ToastProvider, useToast } from "./components/Toast/ToastContext.tsx"
import { ToastContainer } from "./components/Toast/ToastContainer.tsx";
import "./components/Toast/toast.css";
import ManageStore from "./pages/ManageStore/ManageStore.tsx";
import "primereact/resources/themes/lara-light-cyan/theme.css";
import ClientQuery from "./pages/ClientQueryPage/ClientQuery.tsx";
import EvidenceRequestPage from "./pages/ClientQueryPage/EvidenceRequestPage.tsx";
import CustomQueryPage from "./pages/ClientQueryPage/CustomQueryPage.tsx";
import ReportDetails from "./components/ReportDetails.tsx";
import "primereact/resources/themes/lara-light-cyan/theme.css";
import ReportSummaryPage from "./pages/ReportSummary/ReportSummaryPage.tsx";
import "primereact/resources/themes/lara-light-cyan/theme.css";
import LiveView from "./pages/LiveViewPageTrial/LivePageGridView.tsx";
import MiniDrawer from "../src/components/MiniDrawer.tsx";
import ErrorPage from "../src/pages/ErrorPage/ErrorPage.tsx";
import ContactPage from "../src/pages/Contact/ContactPage.tsx";
import CashierFraudPage from "../src/pages/CashierFraud/CashierFraudPage.tsx";
import { NotificationProvider } from "./context/NotificationContext.tsx";
import ProtectedRoute from "./ProtectedRoute.tsx";
import DevicesDashboard from "../src/pages/DevicesPage/DevicesDashboard.tsx";
import FAQPage from "../src/pages/FAQPage/FaqPage.tsx";
import { ThemeProvider } from "@mui/material";
import { theme } from "../src/components/ui/theme.ts";
import useTheme from "./pages/hooks/useTheme.ts";

function getPageTitle(pathname) {
  switch (pathname) {
    case "/":
      return "Login";
    case "/login":
      return "Login";
    case "/forgot-pwd":
      return "Forgot Password";
    case "/reset-pwd":
      return "Reset Password";
    case "/contact-us":
      return "Contact Us";
    case "/home":
      return "Dashboard";
    case "/transaction-alert":
      return "Transaction Alert";
    case "/trigger-alerts":
      return "Trigger Alert";
    case "/profile":
      return "Profile";
    case "/change-pwd":
      return "Change Password";
    case "/manage-stores":
      return "Manage Stores";
    case "/report-summary":
      return "Report Summary";
    case "/client-query":
      return "Client Query";
    case "/live":
      return "Live View";
    case "/devices":
      return "Devices";
    case "/faq":
      return "Frequently Asked Questions";
    case "*":
      return "Error";
    case "/cashier-fraud":
      return "Cashier Fraud";
    default:
      break;
  }

  // Handling dynamic paths
  if (pathname.match(/^\/report\/.+$/)) {
    return "Report Details";
  } else if (pathname.match(/^\/custom-request\/.+$/)) {
    return "Custom Request";
  } else if (pathname.match(/^\/evidence-request\/.+$/)) {
    return "Evidence Request";
  }
  // Default title for any other path return 'Live Eye Surveillance';
  return "Live Eye Surveillance";
}

function RoutesComponent() {
  const location = useLocation();

  const pageTitle = getPageTitle(location.pathname);
  document.title = pageTitle;

  useEffect(() => {
    const userId = localStorage.getItem("user_id");
    if (window.dataLayer) {
      // Push page view event to the GTM data layer
      window.dataLayer.push({
        event: "page_view",
        page_path: location.pathname, // Current page path
        page_title: pageTitle,
        custom_user_id: userId,
      });
    }
  }, [location]);

  return (
    <>
      <Routes>
        <Route path="/" element={<LoginPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-pwd" element={<ForgotPwd />} />
        <Route path="/reset-pwd" element={<ResetPwd />} />

        <Route element={<ProtectedRoute />}>
          <Route path="" element={<MiniDrawer />}>
            <Route path="/contact-us" element={<ContactPage />} />
            <Route path="/home" element={<Dashboard />} />
            <Route path="*" element={<ErrorPage />} />
            <Route path="/report/:_id" element={<ReportDetails />} />
            <Route path="/transaction-alert" element={<SuspiciousAlert />} />
            <Route path="/trigger-alerts" element={<TriggerAlertPage />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/change-pwd" element={<ChangePassword />} />
            <Route path="/manage-stores" element={<ManageStore />} />
            <Route path="/report-summary" element={<ReportSummaryPage />} />
            <Route path="/client-query" element={<ClientQuery />} />
            <Route
              path="/devices"
              element={
                <WebSocketContextProvider>
                  <DevicesDashboard />
                </WebSocketContextProvider>
              }
            />
            <Route path="/faq" element={<FAQPage />} />
            <Route
              path="/custom-request/:customRequestId"
              element={<CustomQueryPage />}
            />
            <Route path="/live" element={<LiveView />} />
            <Route
              path="/evidence-request/:evidenceRequestId"
              element={<EvidenceRequestPage />}
            />
            <Route path="/cashier-fraud" element={<CashierFraudPage />} />
          </Route>
        </Route>
      </Routes>
    </>
  );
}

function App() {
  const { isDark } = useTheme();

  useEffect(() => {
    const root = document.documentElement;
    if (isDark) {
      root.classList.add("dark");
    } else {
      root.classList.remove("dark");
    }
  }, [isDark]);

  return (
    <>
      <ToastProvider>
        <ToastContainer position="top-right" />
        <AuthProvider>
          <UserProvider>
            <DevicesContextProvider>
              <NotificationProvider>
                <LiveViewDataContextProvider>
                  <ClientQueryContextProvider>
                    <ThemeProvider theme={theme}>
                      <RoutesComponent />
                    </ThemeProvider>
                  </ClientQueryContextProvider>
                </LiveViewDataContextProvider>
              </NotificationProvider>
            </DevicesContextProvider>
          </UserProvider>
        </AuthProvider>
      </ToastProvider>
    </>
  );
}

export default App;
